import React from "react";
import {
  SearchIcon,
  EyeIcon,
  BrainIcon,
  PencilLineIcon,
} from "lucide-react";
import { DealThreadMessageStatus } from "@/src/graphql/generated";
import { AnimatePresence, motion } from "framer-motion";
import { classNames } from "@/src/utils/cn";

export const AiLoadingMessageStatusIconMap: Record<
  DealThreadMessageStatus,
  React.ComponentType<{ className?: string }>
> = {
  [DealThreadMessageStatus.Searching]: SearchIcon,
  [DealThreadMessageStatus.Reading]: EyeIcon,
  [DealThreadMessageStatus.Thinking]: BrainIcon,
  [DealThreadMessageStatus.Refining]: PencilLineIcon,
  [DealThreadMessageStatus.Cancelled]: PencilLineIcon,
  [DealThreadMessageStatus.Sent]: PencilLineIcon,
  [DealThreadMessageStatus.Sending]: PencilLineIcon,
  [DealThreadMessageStatus.Verifying]: PencilLineIcon,
  [DealThreadMessageStatus.Failed]: PencilLineIcon,
};

export function AiMessageLoading(props: {
  status: DealThreadMessageStatus;
  text: string;
  animateText?: boolean;
}) {
  const boxSize = 20;
  const iconToRender = AiLoadingMessageStatusIconMap[props.status];
  return (
    <div className={`inline-flex items-center gap-2`}>
      <div
        className="relative overflow-hidden"
        style={{ height: boxSize, width: boxSize }}
        aria-hidden
      >
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={props.status}
            className="absolute inset-0 flex items-center justify-center"
            initial={{ y: boxSize, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -boxSize, opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 34,
              mass: 0.7,
            }}
          >
            {(() => {
              const IconComponent = iconToRender;
              return <IconComponent className="w-4 h-4 text-persian-900" />;
            })()}
          </motion.div>
        </AnimatePresence>
      </div>
      <p
        className={classNames(
          "text-sm font-semibold tracking-tight sm:text-sm",
          props.animateText === false
            ? "text-concrete-700"
            : `animate-shine !bg-clip-text text-transparent
        [background:radial-gradient(circle_at_center,theme(colors.persian-100_/_85%),transparent)_-200%_50%_/_200%_100%_no-repeat,theme(colors.persian-700)]
        dark:[background:radial-gradient(circle_at_center,theme(colors.persian-900_/_85%),transparent)_-200%_50%_/_200%_100%_no-repeat,theme(colors.persian-300)]`,
        )}
      >
        {props.text}
      </p>
    </div>
  );
}

export const animatedEmphasisClassName = `text-sm font-semibold tracking-tight sm:text-sm
animate-shine !bg-clip-text text-transparent
[background:radial-gradient(circle_at_center,theme(colors.persian-100_/_85%),transparent)_-200%_50%_/_200%_100%_no-repeat,theme(colors.persian-700)]
dark:[background:radial-gradient(circle_at_center,theme(colors.persian-900_/_85%),transparent)_-200%_50%_/_200%_100%_no-repeat,theme(colors.persian-300)]`;
